import classNames from "classnames";
import { ActionIcon } from "PFComponents/action_icon";
import InlineCalendar from "PFComponents/calendar/inline_calendar";
import { ErrorMessage } from "PFComponents/error_message/error_message";
import { ExceptionDay } from "PFTypes/booking/booking_config";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CalendarForm } from "../../../../calendars.types";
import { INITIAL_END_TIME, INITIAL_START_TIME, WORKDAY_MINUTES } from "../../../../calendars.utils";
import css from "../../dates.module.scss";
import { DayToggleButton } from "../day_toggle_button";
import { TimeSection } from "../time_section";
import { extractDateErrorMessage, extractErrorMessage } from "../utils";
import { CustomDayOffLabel } from "./custom_day_off_label";

const INITIAL_WEEKEND_START_TIME = "00:00";
const INITIAL_WEEKEND_END_TIME = "23:59";
const INITIAL_WEEKEND_WORKDAY_MINUTES = 0;

type CustomDayOffProps = {
  index: number;
  onRemove: (index: number, exceptionDay: ExceptionDay | undefined) => void;
};

export const CustomDayOff = ({ index, onRemove }: CustomDayOffProps) => {
  const { t } = useTranslation("bookingModule");
  const {
    formState: { errors },
    setValue,
    getValues,
    watch,
    clearErrors
  } = useFormContext<CalendarForm>();

  const kind = watch(`exceptionDays.${index}.kind`);

  const [isDisabled, setIsDisabled] = useState(kind === "holiday");

  const handleDelete = () => {
    const exceptionDay = getValues(`exceptionDays.${index}`);
    const { id, calendarId } = exceptionDay;
    onRemove(index, id && calendarId ? exceptionDay : undefined);
  };

  const handleToggle = () => {
    const updatedIsDisabled = !isDisabled;
    setValue(
      `exceptionDays.${index}.startTime`,
      updatedIsDisabled ? INITIAL_WEEKEND_START_TIME : INITIAL_START_TIME
    );
    setValue(
      `exceptionDays.${index}.endTime`,
      updatedIsDisabled ? INITIAL_WEEKEND_END_TIME : INITIAL_END_TIME
    );
    setValue(
      `exceptionDays.${index}.workingMinutes`,
      updatedIsDisabled ? INITIAL_WEEKEND_WORKDAY_MINUTES : WORKDAY_MINUTES
    );
    setValue(`exceptionDays.${index}.kind`, updatedIsDisabled ? "holiday" : "working_weekend");
    setIsDisabled(updatedIsDisabled);
  };

  const errorMessage = extractErrorMessage(index, errors, "exceptionDays");
  const dateErrorMessage = extractDateErrorMessage(index, errors, "exceptionDays");
  const hasError = Boolean(dateErrorMessage);

  return (
    <div className={css.dayContainer}>
      <div className={classNames(css.sheduleItem, css.exceptionDayItem)}>
        <CustomDayOffLabel index={index} />
        <DayToggleButton
          isDisabled={isDisabled}
          onToggle={handleToggle}
          tooltipMessage={t("config.calendars.exceptionDays.toggleTooltip")}
        />
        <div className={classNames(css.calendarWrapper, { [css.inputError]: hasError })}>
          <Controller
            name={`exceptionDays.${index}.date`}
            rules={{ required: { value: true, message: t("config.calendars.exceptionDays.required") } }}
            render={({ field }) => (
              <InlineCalendar
                placeholder={t("config.calendars.exceptionDays.datePlaceholder")}
                selectedDate={field.value}
                qaId="cudstom-day-off-form-calendar"
                handleChange={(value) => {
                  field.onChange(value);
                  clearErrors(`exceptionDays.${index}.date`);
                }}
                rootCalendarClassName={null}
                calendarProps={null}
                calendarStyle={{ border: "none" }}
                popperOptions={null}
              />
            )}
          />
        </div>
        <TimeSection index={index} daysType="exceptionDays" isDisabled={isDisabled} />
        <ActionIcon name="remove" onClick={handleDelete} />
      </div>
      {dateErrorMessage && <ErrorMessage message={dateErrorMessage} />}
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  );
};
